import { ReactNode } from 'react'
import { X, ShoppingCart, Check, Plus, Minus, FileText, Globe, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Product, ProductVariant } from '../types'
import { formatCurrency, sanitizeHtml } from '../utils/helpers'

interface Props {
  product: Product
  /** Rendered <img> or fallback icon for the product image */
  imageNode: ReactNode
  onClose: () => void

  // Cart
  quantity: number
  onQuantityChange: (qty: number) => void
  onAddToCart: () => void
  isAdded: boolean

  // Variants (optional — catalog only)
  variants?: ProductVariant[] | null // null = loading
  selectedVariantId?: string
  onVariantChange?: (id: string) => void
  variantDescription?: string

  // Extra actions (optional — catalog only)
  onViewDocs?: () => void
}

export default function ProductDetailModal({
  product,
  imageNode,
  onClose,
  quantity,
  onQuantityChange,
  onAddToCart,
  isAdded,
  variants,
  selectedVariantId,
  onVariantChange,
  variantDescription,
  onViewDocs,
}: Props) {
  const { t } = useTranslation()

  // Determine effective price (from selected variant if any)
  let displayPrice = { price: product.price, price_ttc: product.price_ttc }
  if (variants && selectedVariantId) {
    const v = variants.find((v) => v.id === selectedVariantId)
    if (v) {
      displayPrice = {
        price: v.price || product.price,
        price_ttc: v.price_ttc || product.price_ttc,
      }
    }
  }

  const canAddToCart =
    product.stock !== 0 &&
    !(product.has_variants && (variants == null || variants.length === 0 || !selectedVariantId))

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 pr-4">
            <h2 className="text-xl font-bold text-gray-900">{product.label}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('catalog.ref', { ref: product.ref })}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                product.type === 0 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
              }`}
            >
              {product.type === 0 ? t('catalog.typeProduct') : t('catalog.typeService')}
            </span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          {/* Image */}
          <div className="h-64 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
            {imageNode}
          </div>

          <div className="p-6 space-y-5">
            {/* Description */}
            {product.description && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {t('catalog.description', 'Description')}
                </h3>
                <div className="text-sm text-gray-700 leading-relaxed product-description">
                  {product.description.includes('<') ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} />
                  ) : (
                    <p className="whitespace-pre-line">{product.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Variant description */}
            {variantDescription && (
              <div className="text-sm text-gray-600 italic border-l-2 border-primary-200 pl-3 leading-relaxed">
                {variantDescription.includes('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(variantDescription) }} />
                ) : (
                  <p className="whitespace-pre-line">{variantDescription}</p>
                )}
              </div>
            )}

            {/* Variant selector */}
            {product.has_variants && (
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                  <Layers className="w-4 h-4" />
                  {t('catalog.selectVariant')}
                </label>
                {variants == null ? (
                  <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                ) : variants.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">{t('catalog.noVariants')}</p>
                ) : (
                  <select
                    value={selectedVariantId ?? ''}
                    onChange={(e) => onVariantChange?.(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Price — hidden when price is 0 (on-request pricing) */}
            {displayPrice.price_ttc > 0 && (
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">{t('catalog.priceExclTax')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(displayPrice.price)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{t('catalog.priceInclTax')}</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatCurrency(displayPrice.price_ttc)}
                  </p>
                </div>
              </div>
            )}

            {/* Stock */}
            {product.stock !== null && product.stock !== undefined && (
              <p className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {product.stock > 0
                  ? t('catalog.inStock', { count: product.stock })
                  : t('catalog.outOfStock')}
              </p>
            )}

            {/* Extra links */}
            <div className="flex flex-wrap gap-2">
              {product.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  {t('catalog.productWebsite')}
                </a>
              )}
              {onViewDocs && (
                <button
                  onClick={() => {
                    onClose()
                    onViewDocs()
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-primary-600 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  {t('catalog.documentation')}
                </button>
              )}
            </div>

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onQuantityChange(quantity - 1)}
                  disabled={quantity <= 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => {
                    const v = parseInt(e.target.value)
                    if (!isNaN(v) && v >= 1) onQuantityChange(v)
                  }}
                  className="w-16 text-center px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                />
                <button
                  onClick={() => onQuantityChange(quantity + 1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={onAddToCart}
                disabled={!canAddToCart}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  isAdded
                    ? 'bg-green-600 text-white'
                    : !canAddToCart
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {isAdded ? (
                  <>
                    <Check className="w-5 h-5" />
                    <span>{t('catalog.addedToCart')}</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    <span>{t('catalog.addToCart')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
